import React, { useEffect, useMemo, useState } from "react";
import { Box } from "@mui/system";
import { useNavigate } from "react-router-dom";
import { Button } from "../../../../components/Buttons";
import TypographyInline from "../../../../components/Typography/TypographyInline";
import IconComponent from "../../../../components/Icon/IconComponent";
import CustomTooltip from "../../../../components/Tooltip/CustomTooltip";
import { AutoCompleteCustom } from "../../../../components/Autocomplete/AutoCompleteCustom";
import SkeletonLoader from "../../../../components/Loaders/SkeletonLoader";
import StatusChip from "../../../../components/StatusChip/StatusChip";
import EventEmitter from "../../../../utils/emitter/EventEmitter";
import { EMITTER_ERROR } from "../../../../utils/constants";
import {
  authEndpoints,
  CampaignSettingsEndpoints,
} from "../../../../utils/constants/httpConstants";
import { getRequest } from "../../../../Apis/apiWrapper";
import { getErrorMessage } from "../../../../utils/helpers";
import { GREY, WARNING } from "../../../../utils/constants/colors";
import { fontWeight, ICON_LABELS } from "../../../../utils/constants/UI";
import { STRINGS } from "../../../Marketing/strings";
import { styles } from "./styles";
import SignatureFormModal from "./SignatureFormModal";
import DeleteSignatureModal from "./DeleteSignatureModal";
import SetDefaultSignatureModal from "./SetDefaultSignatureModal";

const ALL_MAILBOXES = "__all__";

const MONTHS_SHORT = [
  "Jan", "Feb", "Mar", "Apr", "May", "Jun",
  "Jul", "Aug", "Sep", "Oct", "Nov", "Dec",
];

const formatTimestamp = (input) => {
  if (!input) return "-";
  const d = new Date(input);
  if (isNaN(d.getTime())) return "-";
  let h = d.getHours();
  const m = String(d.getMinutes()).padStart(2, "0");
  const ampm = h >= 12 ? "PM" : "AM";
  h = h % 12 || 12;
  return `${MONTHS_SHORT[d.getMonth()]} ${d.getDate()}, ${d.getFullYear()} · ${String(
    h,
  ).padStart(2, "0")}:${m} ${ampm}`;
};

const flattenInboxes = (apiDetails) => {
  const gmail = apiDetails?.inboxes?.gmail || [];
  const outlook = apiDetails?.inboxes?.outlook || [];
  const zoho = apiDetails?.inboxes?.zoho || [];
  return [...gmail, ...outlook, ...zoho]
    .filter((m) => m && m._id)
    .map((m) => ({
      id: m._id,
      label: m.email,
      email: m.email,
      // send-as alias: display-only; signatures stay keyed to the mailbox email
      alias: m.send_as_alias || null,
      is_primary: !!m.is_primary,
      provider: m.connection_type,
    }));
};

const CampaignSignatures = () => {
  const classes = styles();
  const navigate = useNavigate();

  const [mailboxes, setMailboxes] = useState([]);
  // null = "All mailboxes" mode
  const [selectedAccountId, setSelectedAccountId] = useState(null);
  const [selectedAccountLabel, setSelectedAccountLabel] = useState([
    STRINGS.ALL_MAILBOXES,
  ]);
  const [signatures, setSignatures] = useState([]);
  const [isLoadingMailboxes, setIsLoadingMailboxes] = useState(false);
  const [isLoadingSignatures, setIsLoadingSignatures] = useState(false);
  // Signature count per mailbox account_id across ALL mailboxes. Fetched
  // independently of the selected-mailbox filter so the header counts and the
  // "no signature" badge stay accurate even while a single mailbox is selected.
  const [sigCountByAccount, setSigCountByAccount] = useState({});

  const [formOpen, setFormOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [setDefaultTarget, setSetDefaultTarget] = useState(null);

  const selectedMailbox = useMemo(
    () => mailboxes.find((m) => m.id === selectedAccountId),
    [mailboxes, selectedAccountId],
  );

  const fetchMailboxes = async () => {
    try {
      setIsLoadingMailboxes(true);
      const response = await getRequest(
        navigate,
        authEndpoints.getApiKeyDetails,
      );
      const flat = flattenInboxes(response?.data?.data);
      setMailboxes(flat);
    } catch (err) {
      EventEmitter.emit(
        EMITTER_ERROR,
        getErrorMessage(err?.data?.data?.errors || {}, err?.data?.message),
      );
    } finally {
      setIsLoadingMailboxes(false);
    }
  };

  // Fetches signatures for one mailbox (flat array) or all mailboxes
  // (server returns a map keyed by account_id, which we flatten and tag with email).
  const fetchSignatures = async (accountId) => {
    try {
      setIsLoadingSignatures(true);
      // Drop stale signatures from the previous mailbox so gating checks
      // (e.g. hasDefaultForSelectedMailbox) can't briefly read old data.
      setSignatures([]);
      const url = accountId
        ? `${CampaignSettingsEndpoints.signatureList}?account_id=${accountId}`
        : CampaignSettingsEndpoints.signatureList;
      const response = await getRequest(navigate, url);
      const data = response?.data?.data;

      let list = [];
      if (Array.isArray(data)) {
        list = data;
      } else if (data && typeof data === "object") {
        list = Object.values(data).flatMap((entry) =>
          (entry?.signatures || []).map((s) => ({
            ...s,
            account_id: s.account_id || entry?.account_id,
            email: s.email || entry?.email,
          })),
        );
      }
      setSignatures(list);
    } catch (err) {
      EventEmitter.emit(
        EMITTER_ERROR,
        getErrorMessage(err?.data?.data?.errors || {}, err?.data?.message),
      );
      setSignatures([]);
    } finally {
      setIsLoadingSignatures(false);
    }
  };

  // Fetches signatures for EVERY mailbox and tallies per-account counts. Used
  // for the header "signatures added" count and the per-mailbox "no signature"
  // badge — independent of the currently selected mailbox filter.
  const fetchAllSignatureCounts = async () => {
    try {
      const response = await getRequest(
        navigate,
        CampaignSettingsEndpoints.signatureList,
      );
      const data = response?.data?.data;
      const counts = {};
      if (Array.isArray(data)) {
        // Flat array (only when account_id is passed — not our case, but keep
        // it defensive). Tally by each signature's account_id.
        data.forEach((s) => {
          const key = s?.account_id;
          if (key) counts[key] = (counts[key] || 0) + 1;
        });
      } else if (data && typeof data === "object") {
        // All-mailboxes map keyed by account_id. Prefer the map key as the
        // account_id (entry.account_id can be absent), fall back to the entry.
        Object.entries(data).forEach(([mapKey, entry]) => {
          const key = entry?.account_id || mapKey;
          const n = entry?.signatures?.length || 0;
          if (key) counts[key] = (counts[key] || 0) + n;
        });
      }
      setSigCountByAccount(counts);
    } catch (err) {
      // Non-blocking: the screen still works without the header counts/badge.
      setSigCountByAccount({});
    }
  };

  useEffect(() => {
    fetchMailboxes();
    fetchAllSignatureCounts();
  }, []);

  useEffect(() => {
    fetchSignatures(selectedAccountId);
  }, [selectedAccountId]);

  const handleMailboxChange = ({ id, label }) => {
    const newId = id?.[0];
    const newLabel = label?.[0];
    if (newId === ALL_MAILBOXES) {
      setSelectedAccountId(null);
      setSelectedAccountLabel([STRINGS.ALL_MAILBOXES]);
      return;
    }
    if (newId && newId !== selectedAccountId) {
      setSelectedAccountId(newId);
      setSelectedAccountLabel(newLabel ? [newLabel] : []);
    }
  };

  const mailboxItems = useMemo(
    () => [
      { id: ALL_MAILBOXES, label: STRINGS.ALL_MAILBOXES },
      // dropdown shows the sending address (alias when picked); id stays the
      // mailbox record, so filtering/signature lookups are unchanged
      ...mailboxes.map((m) => ({ id: m.id, label: m.alias || m.email })),
    ],
    [mailboxes],
  );

  const openCreate = () => {
    if (!selectedAccountId || isLoadingSignatures) return;
    if (
      signatures.some(
        (s) => s.account_id === selectedAccountId && s.is_default,
      )
    ) {
      return;
    }
    setEditingId(null);
    setFormOpen(true);
  };

  const [editingMailbox, setEditingMailbox] = useState(null);

  const openEdit = (sig) => {
    setEditingId(sig._id);
    const mbForSig =
      mailboxes.find((m) => m.id === sig.account_id) ||
      (sig.email
        ? mailboxes.find(
            (m) => (m.email || "").toLowerCase() === sig.email.toLowerCase(),
          )
        : null);
    setEditingMailbox(
      mbForSig ||
        (sig.email
          ? { id: sig.account_id, email: sig.email, provider: sig.provider }
          : null),
    );
    setFormOpen(true);
  };

  // Opens the confirmation modal - the actual /signature/set-default call
  // happens inside SetDefaultSignatureModal once the user confirms.
  const handleSetDefault = (sig) => {
    if (sig.is_default) return;
    setSetDefaultTarget(sig);
  };

  // Default is mailbox-scoped, so only flip flags within the same mailbox.
  const handleSetDefaultConfirmed = (sig) => {
    setSignatures((prev) =>
      prev.map((s) =>
        s.account_id === sig.account_id
          ? { ...s, is_default: s._id === sig._id }
          : s,
      ),
    );
  };

  const handleSaved = () => {
    fetchSignatures(selectedAccountId);
    fetchAllSignatureCounts();
  };
  const handleDeleted = () => {
    fetchSignatures(selectedAccountId);
    fetchAllSignatureCounts();
  };

  // For the "first signature for this mailbox" auto-default UX, only check
  // signatures belonging to the currently selected mailbox.
  const isFirstForMailbox =
    !!selectedAccountId &&
    signatures.filter((s) => s.account_id === selectedAccountId).length === 0;

  // Only the default signature per mailbox is surfaced in this view; once one
  // exists, creating another for the same mailbox is blocked.
  const visibleSignatures = useMemo(
    () => signatures.filter((s) => s.is_default),
    [signatures],
  );

  const hasDefaultForSelectedMailbox =
    !!selectedAccountId &&
    signatures.some(
      (s) => s.account_id === selectedAccountId && s.is_default,
    );

  // Header counts (across all mailboxes). "Signatures added" is the number of
  // mailboxes that have at least one signature; the missing count is the rest.
  const mailboxesWithSignatureCount = useMemo(
    () => mailboxes.filter((m) => (sigCountByAccount[m.id] || 0) > 0).length,
    [mailboxes, sigCountByAccount],
  );
  const mailboxesMissingSignatureCount = Math.max(
    mailboxes.length - mailboxesWithSignatureCount,
    0,
  );

  // The currently selected mailbox has no signature yet (drives the toolbar
  // "No signature — Add one here" badge).
  const selectedMailboxMissingSignature =
    !!selectedAccountId && (sigCountByAccount[selectedAccountId] || 0) === 0;

  const showMailboxColumn = !selectedAccountId;
  const noMailboxes = !isLoadingMailboxes && mailboxes.length === 0;

  // Blacklist-style column tracks. Reserve ~24px right margin for the actions
  // cell so two icon buttons + the optional Set-default link fit comfortably.
  const gridTemplateColumns = showMailboxColumn
    ? "minmax(180px, 1.4fr) minmax(220px, 1.6fr) minmax(200px, 1.4fr) minmax(170px, 1fr) 120px 140px"
    : "minmax(220px, 1.6fr) minmax(280px, 2fr) minmax(170px, 1fr) 140px 160px";

  if (noMailboxes) {
    return (
      <Box className={classes.signaturesShell}>
        <Box className={classes.signaturesEmpty}>
          <IconComponent
            iconLabel={ICON_LABELS.EMAIL}
            color={GREY.TERTIARY}
            fontSize={"32px"}
          />
          <TypographyInline
            size={"md"}
            color={GREY.PRIMARY}
            fontWeight={fontWeight.MEDIUM}
            label={STRINGS.NO_MAILBOX_TITLE}
          />
          <TypographyInline
            size={"sm"}
            color={GREY.SECONDARY}
            fontWeight={fontWeight.REGULAR}
            label={STRINGS.NO_MAILBOX_DESC}
          />
        </Box>
      </Box>
    );
  }

  return (
    <Box className={classes.sigPage}>
      <Box className={classes.sigToolbar}>
        <Box className={classes.sigToolbarLeft}>
          <AutoCompleteCustom
            items={mailboxItems}
            valueState={selectedAccountLabel}
            setValueState={setSelectedAccountLabel}
            idState={selectedAccountId ? [selectedAccountId] : [ALL_MAILBOXES]}
            setIdState={() => {}}
            onChange={handleMailboxChange}
            size={"md40"}
            isLoading={isLoadingMailboxes}
          />
          {!isLoadingMailboxes && mailboxes.length > 0 && (
            <span className={classes.espCountChip}>
              <span className={classes.espCountChipNum}>
                {mailboxes.length}
              </span>
              {mailboxes.length === 1
                ? STRINGS.MAILBOX_CONNECTED_ONE
                : STRINGS.MAILBOX_CONNECTED_OTHER}
            </span>
          )}
          {!isLoadingMailboxes && mailboxes.length > 0 && (
            <span className={classes.espCountChip}>
              <span className={classes.espCountChipNum}>
                {mailboxesWithSignatureCount}
              </span>
              {mailboxesWithSignatureCount === 1
                ? STRINGS.SIGNATURE_COUNT_ONE
                : STRINGS.SIGNATURE_COUNT_OTHER}
            </span>
          )}
          {/* All-mailboxes view: warn how many mailboxes still lack a signature. */}
          {!isLoadingMailboxes &&
            !selectedAccountId &&
            mailboxesMissingSignatureCount > 0 && (
              <CustomTooltip
                title={STRINGS.MAILBOXES_MISSING_SIGNATURE_HINT}
                variant="top"
              >
                <span className={classes.sigMissingChip}>
                  <IconComponent
                    iconLabel={ICON_LABELS.WARNING}
                    color={WARNING.W_700}
                    fontSize={"14px"}
                  />
                  <span className={classes.sigMissingChipNum}>
                    {mailboxesMissingSignatureCount}
                  </span>
                  {STRINGS.MAILBOXES_MISSING_SIGNATURE}
                </span>
              </CustomTooltip>
            )}
          {/* Selected-mailbox view: badge prompting the user to add one. */}
          {!isLoadingMailboxes &&
            selectedMailboxMissingSignature &&
            !isLoadingSignatures && (
              <span
                className={classes.sigAddBadge}
                onClick={openCreate}
                role="button"
              >
                <IconComponent
                  iconLabel={ICON_LABELS.WARNING}
                  color={WARNING.W_700}
                  fontSize={"14px"}
                />
                {STRINGS.NO_SIGNATURE_BADGE} · {STRINGS.ADD_ONE_HERE}
              </span>
            )}
        </Box>
        <CustomTooltip
          title={
            !selectedAccountId
              ? STRINGS.PICK_MAILBOX_TO_ADD
              : isLoadingSignatures
              ? ""
              : hasDefaultForSelectedMailbox
              ? STRINGS.DEFAULT_SIGNATURE_EXISTS
              : ""
          }
          variant="top"
        >
          <Box>
            <Button
              label={STRINGS.ADD_SIGNATURE}
              LeftIcon={ICON_LABELS.ADD}
              variant={"primary"}
              size={"sm36"}
              onClick={openCreate}
              disabled={
                !selectedAccountId ||
                isLoadingSignatures ||
                hasDefaultForSelectedMailbox
              }
            />
          </Box>
        </CustomTooltip>
      </Box>

      {!isLoadingSignatures && visibleSignatures.length === 0 ? (
        <Box className={classes.signaturesEmpty}>
          <IconComponent
            iconLabel={ICON_LABELS.EMAIL}
            color={GREY.TERTIARY}
            fontSize={"28px"}
          />
          <TypographyInline
            size={"md"}
            color={GREY.PRIMARY}
            fontWeight={fontWeight.MEDIUM}
            label={STRINGS.NO_SIGNATURES_TITLE}
          />
          <TypographyInline
            size={"sm"}
            color={GREY.SECONDARY}
            fontWeight={fontWeight.REGULAR}
            label={STRINGS.NO_SIGNATURES_DESC}
          />
        </Box>
      ) : (
        <Box className={classes.blkTableShell}>
          <Box
            className={classes.blkTableHead}
            style={{ gridTemplateColumns }}
          >
            <span className={classes.blkTableHeadCell}>
              {STRINGS.COL_NAME}
            </span>
            <span className={classes.blkTableHeadCell}>
              {STRINGS.COL_SENDER}
            </span>
            {showMailboxColumn && (
              <span className={classes.blkTableHeadCell}>
                {STRINGS.COL_MAILBOX}
              </span>
            )}
            <span className={classes.blkTableHeadCell}>
              {STRINGS.COL_UPDATED_AT}
            </span>
            <span className={classes.blkTableHeadCell}>
              {STRINGS.COL_DEFAULT}
            </span>
            <span className={classes.blkTableHeadCell}>
              {STRINGS.COL_ACTIONS}
            </span>
          </Box>
          <Box className={classes.blkTableBody}>
            {isLoadingSignatures && visibleSignatures.length === 0
              ? [1, 2, 3].map((n) => (
                  <Box
                    key={`skeleton-${n}`}
                    className={classes.blkTableRow}
                    style={{ gridTemplateColumns }}
                  >
                    <SkeletonLoader
                      type="rounded"
                      skeletonWidth={"60%"}
                      skeletonHeight={14}
                    />
                    <SkeletonLoader
                      type="rounded"
                      skeletonWidth={"80%"}
                      skeletonHeight={14}
                    />
                    {showMailboxColumn && (
                      <SkeletonLoader
                        type="rounded"
                        skeletonWidth={"70%"}
                        skeletonHeight={14}
                      />
                    )}
                    <SkeletonLoader
                      type="rounded"
                      skeletonWidth={"75%"}
                      skeletonHeight={14}
                    />
                    <SkeletonLoader
                      type="rounded"
                      skeletonWidth={"50%"}
                      skeletonHeight={14}
                    />
                    <SkeletonLoader
                      type="rounded"
                      skeletonWidth={"40%"}
                      skeletonHeight={14}
                    />
                  </Box>
                ))
              : null}
            {visibleSignatures.map((sig) => {
              const senderName = [
                sig.signature?.first_name,
                sig.signature?.last_name,
              ]
                .filter(Boolean)
                .join(" ");
              const senderMeta = [
                sig.signature?.job_title,
                sig.signature?.company_name,
              ]
                .filter(Boolean)
                .join(" · ");
              const senderText =
                [senderName, senderMeta].filter(Boolean).join(" - ") || "-";
              return (
                <Box
                  key={sig._id}
                  className={classes.blkTableRow}
                  style={{ gridTemplateColumns }}
                >
                  <span
                    className={`${classes.blkTableCell} ${classes.blkTableCellName}`}
                    title={sig.signature_name}
                  >
                    {sig.signature_name || "-"}
                  </span>
                  <span className={classes.blkTableCell} title={senderText}>
                    {senderText}
                  </span>
                  {showMailboxColumn && (() => {
                    // Mailbox column shows the sending address: the alias when
                    // that mailbox has one picked, else the mailbox email
                    const mb = mailboxes.find(
                      (m) => (m.email || "").toLowerCase() === (sig.email || "").toLowerCase(),
                    );
                    const shown = mb?.alias || sig.email;
                    return (
                      <span
                        className={classes.blkTableCell}
                        title={mb?.alias ? `Mailbox: ${sig.email}` : sig.email}
                      >
                        {shown || "-"}
                      </span>
                    );
                  })()}
                  <span
                    className={classes.blkTableCell}
                    title={
                      sig.updated_at
                        ? `Updated ${formatTimestamp(sig.updated_at)}${
                            sig.created_at
                              ? `\nCreated ${formatTimestamp(sig.created_at)}`
                              : ""
                          }`
                        : ""
                    }
                  >
                    {formatTimestamp(sig.updated_at || sig.created_at)}
                  </span>
                  <span className={classes.blkTableCell}>
                    {sig.is_default ? (
                      <StatusChip
                        variant="green"
                        label={STRINGS.DEFAULT_BADGE}
                      />
                    ) : (
                      <Button
                        label={STRINGS.SET_AS_DEFAULT}
                        variant={"ghost"}
                        size={"xs28"}
                        onClick={() => handleSetDefault(sig)}
                      />
                    )}
                  </span>
                  <Box className={classes.blkTableActionsCell}>
                    <CustomTooltip title={STRINGS.COMMON_EDIT} variant="top">
                      <Box
                        className={classes.blkTableIconBtn}
                        onClick={() => openEdit(sig)}
                      >
                        <IconComponent
                          iconLabel={ICON_LABELS.EDIT}
                          color={GREY.SECONDARY}
                          fontSize={"16px"}
                        />
                      </Box>
                    </CustomTooltip>
                    <CustomTooltip title={STRINGS.DELETE} variant="top">
                      <Box
                        className={classes.blkTableIconBtn}
                        onClick={() => setDeleteTarget(sig)}
                      >
                        <IconComponent
                          iconLabel={ICON_LABELS.DELETE}
                          color={GREY.SECONDARY}
                          fontSize={"16px"}
                        />
                      </Box>
                    </CustomTooltip>
                  </Box>
                </Box>
              );
            })}
          </Box>
        </Box>
      )}

      {formOpen && (() => {
        const effectiveMailbox =
          editingId && editingMailbox ? editingMailbox : selectedMailbox;
        const effectiveAccountId =
          editingId && editingMailbox?.id
            ? editingMailbox.id
            : selectedAccountId;
        return (
          <SignatureFormModal
            open={formOpen}
            onClose={() => {
              setFormOpen(false);
              setEditingId(null);
              setEditingMailbox(null);
            }}
            onSaved={handleSaved}
            accountId={effectiveAccountId}
            accountEmail={effectiveMailbox?.email}
            accountProvider={effectiveMailbox?.provider}
            signatureId={editingId}
            isFirstForMailbox={isFirstForMailbox && !editingId}
          />
        );
      })()}

      {deleteTarget && (
        <DeleteSignatureModal
          open={Boolean(deleteTarget)}
          onClose={() => setDeleteTarget(null)}
          onDeleted={handleDeleted}
          signature={deleteTarget}
        />
      )}

      {setDefaultTarget && (
        <SetDefaultSignatureModal
          open={Boolean(setDefaultTarget)}
          onClose={() => setSetDefaultTarget(null)}
          onConfirmed={handleSetDefaultConfirmed}
          signature={setDefaultTarget}
        />
      )}
    </Box>
  );
};

export default CampaignSignatures;
